/**
 * Notification templates (Trera and Traci, 27 Sep 2026): the wording of the emails and texts clients and talent get,
 * editable by Admin in Business Controls → Notifications, with an on/off switch each.
 *
 * One file shared by every place that uses it, so they cannot drift:
 *   - the edge functions (send-scheduled-emails, send-photo-upload-reminders) import it from ../_shared/
 *   - the Node server (src/server/services/smsService.js, the admin API) imports it by relative path
 *   - the console imports it for the live preview
 * Pure JS, no imports.
 *
 * The defaults below are the wording that was live on 27 Sep 2026. An Admin edit is a row in the
 * notification_templates table (migration 20260927120000); a sender merges that row over the default, so a missing
 * row, a missing table or a database error always falls back to the wording here.
 *
 * Wording format (what Traci edits), kept deliberately small so an edit cannot break the email:
 *   blank line          new paragraph (a single line break stays a line break)
 *   # Heading           a heading line
 *   - item              a bullet
 *   **bold**            bold
 *   [Label](link)       on its own line: a button; inside a sentence: a link
 *   {{field}}           filled in per message; each template lists the fields it can use
 * The branded frame (header, footer, "replies aren't monitored", the unsubscribe line on offers) is fixed and not
 * part of the wording. Every text ends with "Reply STOP to opt out." whether or not the wording says it.
 */

export const STOP_LINE = 'Reply STOP to opt out.'
export const OFFER_FOOTER = "You're receiving this offer because you booked with Love & Photos. To stop offers like this, email support@lp.loveandphotos.com."
/** The business's mailing address, in every email's footer (Traci, 1 Oct 2026, for compliance: offers must carry one). */
export const BUSINESS_ADDRESS = 'Love & Photos · 3400 Cottage Way #G2, Sacramento, CA 95825'
/** Where talent write when an upload to Box goes wrong (Traci, 1 Oct 2026: "fix that box support email"). */
export const BOX_SUPPORT_EMAIL = 'studio@team.loveandphotos.com'
export const DISREGARD = "If you've already uploaded your media and submitted your form, thank you, and please disregard this email."

const SENDERS = {
  client: 'Love & Photos <support@lp.loveandphotos.com>',
  talent: 'Love & Photos Talent <studio@team.loveandphotos.com>'
}

const f = (name, label, sample, optional = false) => ({ name, label, sample, optional })
const FIRST = f('first_name', 'First name', 'Summer')
const EVENT_DATE = f('event_date', 'Event date', 'Saturday, October 4, 2026')
const CLIENT_PORTAL = f('portal_link', 'Client portal link', 'https://loveandphotos.com/dashboard')
const TALENT_PORTAL = f('portal_link', 'Talent portal link', 'https://loveandphotos.com/talent/dashboard')
const VENDOR_NAME = f('vendor_name', 'Vendor contact name', 'Dana Lee')
const COUPLE = f('couple_name', 'Couple (from the questionnaire)', 'Ana & Ben')

/**
 * Every message the Notifications tab manages. Order = the order they happen in a booking.
 *   group      client | both | talent | vendor
 *   channel    email | sms | chat (a message in the client's or talent's portal chat; plain text)
 *   type       what the sender calls it (email_schedules.email_type / sms_log.sms_type), for the sent counts
 *   offer      a promotional email: gets the unsubscribe footer
 *   footer     'no_reply' (the scheduled emails' "Replies aren't monitored") or 'reply' (replies welcome)
 *   sender     'client' | 'talent' (the usual From addresses), or `from` for an email that keeps its own address
 */

/** Section order in the tab, per group (the catalog below may list a section's messages in any order). */
export const SECTION_ORDER = {
  client: ['Booking and payment', 'Planning the event', 'After the event', 'Changes and cancellations', 'Gifts and referrals', 'Portal chat'],
  both: ['Messages between client and talent'],
  vendor: ['After the wedding'],
  talent: ['Joining the team', 'Getting booked', 'Before the event', 'After the event: uploading media', 'Changes and cancellations', 'Portal chat']
}
export const CATALOG = [
  // ── Clients: booking and payment ──────────────────────────────────────────────────────────────────────────
  {
    key: 'abandoned_booking_reminder', group: 'client', section: 'Booking and payment', channel: 'email', sender: 'client', footer: 'no_reply',
    name: 'Unfinished checkout reminder', audience: 'Client', when: '8 or more hours after a client leaves checkout unfinished. Once.',
    type: 'abandoned_booking_reminder',
    fields: [FIRST, f('package', 'Package', 'Wedding Package', true), f('hours', 'Hours booked', '8', true), f('total', 'Total', '$2,450.00', true), f('booking_link', 'Finish-booking link', 'https://loveandphotos.com/checkout/…')],
    subject: 'Complete Your Love & Photos Booking',
    body: `# Your Dream Wedding Photos Await!

Hi {{first_name}},

We noticed you started booking your wedding photography with Love & Photos, but didn't quite finish. We'd love to help you lock in your date!

Your selected package is waiting for you, and we want to make sure you don't miss out on having the perfect team capture your special day.

**Your Selected Package:**
Package: {{package}}
Coverage: {{hours}} hours
Total: {{total}}

**Important:** This booking will expire in 64 hours if not completed. Don't lose your spot!

[Complete My Booking Now]({{booking_link}})

Join 10,000+ couples who trusted Love & Photos to capture their most precious moments

We're here to make this easy! If you have any questions or need help completing your booking, contact us at support@lp.loveandphotos.com.

We can't wait to be part of your special day!
The Love & Photos Team`
  },
  {
    key: 'sms_booking_confirmed', group: 'client', section: 'Booking and payment', channel: 'sms',
    name: 'Booking confirmed text', audience: 'Client', when: 'At checkout. Once.', type: 'booking_confirmed',
    fields: [EVENT_DATE, f('event_time', 'Start time', '4:00 PM')],
    body: 'Love & Photos LLC: Your booking is confirmed for {{event_date}} at {{event_time}}. Reply STOP to opt out. HELP for help.'
  },
  {
    key: 'sms_failed_payment', group: 'client', section: 'Booking and payment', channel: 'sms',
    name: 'Payment failed text', audience: 'Client', when: 'When a payment fails. Once per booking.', type: 'failed_payment',
    fields: [],
    body: 'Love & Photos: Payment failed. To keep your booking active, please email billing@loveandphotos.com for help updating your payment method.'
  },
  {
    key: 'sms_final_payment', group: 'client', section: 'Booking and payment', channel: 'sms',
    name: 'Final payment tomorrow text', audience: 'Client', when: 'The day before the final payment is charged. Not running today.', type: 'final_payment_due',
    fields: [],
    body: 'Love & Photos: Reminder -- your final payment will be processed tomorrow.'
  },
  // ── Clients: planning ─────────────────────────────────────────────────────────────────────────────────────
  {
    key: 'logistics_initial', group: 'client', section: 'Planning the event', channel: 'email', sender: 'client', footer: 'no_reply',
    name: 'Logistics questionnaire (2 months out)', audience: 'Client', when: '9 AM Central, 60 days before the event (booked closer: within the hour). Once; skipped once submitted.',
    type: 'logistics_initial',
    fields: [FIRST, CLIENT_PORTAL],
    subject: 'Action Needed: Complete Your Wedding Logistics (2 Months Out)',
    body: `Hi {{first_name}},

We're 2 months away from your event—yay! To keep everything smooth, please log into your Client Portal and complete your Logistics Questionnaire. This gives your team the key details (timeline, locations, contacts, preferences) so we can prep and execute flawlessly.

[Complete the Logistics Questionnaire Here]({{portal_link}})

If you need assistance, please contact Love & Photos Support at support@lp.loveandphotos.com

With love,
The Love & Photos Team`
  },
  {
    key: 'sms_logistics', group: 'client', section: 'Planning the event', channel: 'sms',
    name: 'Logistics questionnaire text', audience: 'Client', when: 'The first time the event is within 2 months. Once.', type: 'logistics_questionnaire',
    fields: [f('deadline', 'Complete-by date', 'October 9, 2026')],
    body: 'Love & Photos: Logistics questionnaire now available in your portal. Please complete by {{deadline}} to ensure your booking remains active.'
  },
  {
    key: 'logistics_reminder', group: 'client', section: 'Planning the event', channel: 'email', sender: 'client', footer: 'no_reply',
    name: 'Logistics questionnaire reminder', audience: 'Client', when: '9 AM Central, 57 days before the event, only if not submitted. Once.',
    type: 'logistics_reminder',
    fields: [FIRST, CLIENT_PORTAL],
    subject: 'Reminder: Your Logistics Questionnaire Is Needed',
    body: `Hi {{first_name}},

Friendly nudge—your Logistics Questionnaire still shows as incomplete. Please take a few minutes to fill it out so your team has your day-of details and preferences.

[Complete Now]({{portal_link}})

Completing it ensures accurate timelines, smooth coordination, and proper talent prep — missing info can delay planning.

If you need assistance, please contact Love & Photos Support at support@lp.loveandphotos.com

Thank you!
The Love & Photos Team`
  },
  {
    key: 'chat_open_client', group: 'client', section: 'Planning the event', channel: 'email', sender: 'client', footer: 'no_reply',
    name: 'Team chat is open (1 month out)', audience: 'Client', when: '9 AM Central, 30 days before the event. Once.',
    type: 'chat_open_client',
    fields: [FIRST, EVENT_DATE, CLIENT_PORTAL],
    subject: 'Your Team Chat is Now Open! 💬',
    body: `Hi {{first_name}},

We're officially one month out from your big day on {{event_date}}—yay! 🎉 Your in-portal Chat is now open so you can connect directly with your team for quick introductions and any event-day details.

We're super excited to bring your vision to life!

[Open Your Client Portal]({{portal_link}})

Need help? Contact Support: support@lp.loveandphotos.com

— Love & Photos Team`
  },
  {
    key: 'sms_chat_open_client', group: 'client', section: 'Planning the event', channel: 'sms',
    name: 'Chat is open text', audience: 'Client', when: 'When real talent is assigned and the event is within a month. Once.', type: 'chat_open',
    fields: [],
    body: "Love & Photos LLC: Meet your Talent -- you're now able to chat with your talent in your client portal."
  },
  {
    key: 'one_week_reminder', group: 'client', section: 'Planning the event', channel: 'email', sender: 'client', footer: 'no_reply',
    name: 'One week to go', audience: 'Client', when: '9 AM Central, 7 days before the event. Once.',
    type: 'one_week_reminder',
    fields: [FIRST],
    subject: 'One Week to Go!',
    body: `Hi {{first_name}},

It's almost here — just one week until your big day!

We're so excited to celebrate alongside you and deliver memories you'll treasure for a lifetime. Thank you again for choosing Love & Photos — we can't wait to create magic for you!

With love,
The Love & Photos Team`
  },
  {
    key: 'three_day_confirmation', group: 'client', section: 'Planning the event', channel: 'email', sender: 'client', footer: 'no_reply',
    name: "We're all set (3 days out)", audience: 'Client', when: '9 AM Central, 3 days before the event. Once.',
    type: 'three_day_confirmation',
    fields: [FIRST],
    subject: "We're All Set for Your Big Day!",
    body: `Hi {{first_name}},

Your wedding day is almost here, and we just wanted to let you know — everything is confirmed and we're all set!

Your Love & Photos team has reviewed your details and is fully prepared to capture every moment beautifully. You can relax knowing everything is in place for a smooth, unforgettable experience.

We're so honored to be part of your day and can't wait to celebrate with you soon!

With love,
The Love & Photos Team`
  },
  {
    key: 'sms_one_day', group: 'client', section: 'Planning the event', channel: 'sms',
    name: 'Tomorrow reminder text', audience: 'Client', when: 'About 10 AM Mountain, the day before. Once.', type: 'one_day_reminder',
    fields: [f('event_time', 'Start time', '4:00 PM')],
    body: 'Love & Photos LLC: Reminder -- your booking is tomorrow at {{event_time}}.'
  },
  {
    key: 'sms_event_day', group: 'client', section: 'Planning the event', channel: 'sms',
    name: 'Today reminder text', audience: 'Client', when: 'About 10 AM Mountain, the event day. Once.', type: 'event_day_reminder',
    fields: [f('event_time', 'Start time', '4:00 PM')],
    body: 'Love & Photos LLC: Reminder -- your booking is today at {{event_time}}.'
  },
  // ── Clients: after the event ─────────────────────────────────────────────────────────────────────────────
  {
    key: 'post_event_congrats', group: 'client', section: 'After the event', channel: 'email', sender: 'client', footer: 'no_reply', offer: true,
    name: 'Congratulations + Rush Delivery', audience: 'Client', when: '9 AM Central, the day after the event. Once.',
    type: 'post_event_congrats',
    fields: [FIRST, CLIENT_PORTAL],
    subject: 'Congratulations Again!',
    body: `Hi {{first_name}},

Congratulations! 🥂 We hope your day was everything you dreamed of and more — filled with laughter, love, and unforgettable memories.

Our team is already preparing your media for editing and delivery. Standard delivery is up to 3 months for photos and up to 4 months for video. You now have the option to upgrade to Rush Delivery if you'd like your media delivered much sooner.

[Upgrade to Rush Delivery HERE]({{portal_link}})

Rush options are only available for a limited time (within 48 hours after your event), so if you'd like faster turnaround, be sure to lock it in soon!

Thank you again for trusting Love & Photos with your day — it was an honor!

Warmly,
The Love & Photos Team`
  },
  {
    key: 'sneak_peek_offer', group: 'client', section: 'After the event', channel: 'email', sender: 'client', footer: 'no_reply', offer: true,
    name: 'Sneak Peek offer', audience: 'Client', when: '9 AM Central, the day after the event. Once.',
    type: 'sneak_peek_offer',
    fields: [FIRST, CLIENT_PORTAL],
    subject: '✨ Get a Sneak Peek of Your Wedding Photos!',
    body: `# ✨ Sneak Peek Photos Available!

Hi {{first_name}},

Congratulations again on your beautiful wedding day! 🎉 We know the anticipation to see your photos is real, so we're excited to offer you something special.

**📸 Sneak Peek Photos**
10 professionally edited highlight photos
Delivered in just 7 days
$195

While your full gallery is being carefully edited (standard photo delivery is up to 3 months), you can get a preview of your favorite moments much sooner with our Sneak Peek package.

**What's Included:**
- 10 hand-selected highlight photos from your wedding day
- Professionally edited to perfection
- High-resolution digital files
- Ready to share with friends and family
- Delivered within 7 days of purchase

[Get Your Sneak Peek Photos]({{portal_link}})

This offer is available for a limited time. Visit your dashboard to add Sneak Peek Photos to your order today!

We can't wait for you to see the beautiful memories we captured!

Questions? Contact Support: support@lp.loveandphotos.com

With love,
The Love & Photos Team`
  },
  {
    key: 'review_request_standard', group: 'client', section: 'After the event', channel: 'email', sender: 'client', footer: 'no_reply', offer: true,
    name: 'Review request', audience: 'Client', when: '9 AM Central, 7 days after the event. Once. (About 45% of bookings without Rush get the expedited version below instead.)',
    type: 'review_request',
    fields: [FIRST, f('review_link', 'Review page link', 'https://loveandphotos.com/review/…')],
    subject: 'Share Your Love & Photos Experience',
    body: `# Share Your Experience

Hi {{first_name}},

We hope you're still smiling from your day! Your Love & Photos team loved being part of your celebration.

[Leave Your Review]({{review_link}})

**What to Include in Your Review:**
- How was your overall experience?
- Were your photographer and videographer professional?
- Did they capture the moments you wanted?
- Would you recommend Love & Photos to other couples?

Your honest feedback helps us celebrate our amazing talent and continue improving. Thank you for choosing Love & Photos!

With gratitude,
The Love & Photos Team`
  },
  {
    key: 'review_request_expedited', group: 'client', section: 'After the event', channel: 'email', sender: 'client', footer: 'no_reply', offer: true,
    name: 'Review request (expedited delivery version)', audience: 'Client', when: '9 AM Central, 7 days after the event, for about 45% of bookings without Rush. Once.',
    fields: [FIRST, f('google_link', 'Google review link', 'https://www.google.com/…'), f('yelp_link', 'Yelp review link', 'https://www.yelp.com/…'), f('knot_link', 'The Knot review link', 'https://www.theknot.com/…')],
    subject: 'Share Your Reviews & Receive Expedited Delivery',
    body: `# A Special Thank You

Hi {{first_name}},

We hope you're still smiling from your day! Your Love & Photos team loved being part of your celebration.

**Expedited Delivery Offer**
Complete a review on Google, Yelp, and/or The Knot and we will move your order up in the editing queue.
This is not Rush Delivery; internal target for review-based expedited delivery is 4 weeks.

[Review on Google]({{google_link}})

[Review on Yelp]({{yelp_link}})

[Review on The Knot]({{knot_link}})

After you complete a public review, our team will verify it and apply the expedited editing queue note internally.

With gratitude,
The Love & Photos Team`
  },
  {
    key: 'sms_album_delivered', group: 'client', section: 'After the event', channel: 'sms',
    name: 'Gallery delivered text', audience: 'Client', when: 'When the gallery is delivered. Not running today.', type: 'album_delivered',
    fields: [f('media_type', 'Photo / video', 'photo')],
    body: 'Love & Photos LLC: Reminder -- your {{media_type}} gallery has been delivered via email!'
  },
  {
    key: 'sms_gift_session', group: 'client', section: 'After the event', channel: 'sms',
    name: 'Gift session text', audience: 'Client', when: 'When staff gift a session. Once.', type: 'gift_session_granted',
    fields: [],
    body: 'Love & Photos: Great news -- you have been gifted a complimentary photography session! Log into your client portal at loveandphotos.com/dashboard to choose your date and location.'
  },
  // ── Both ─────────────────────────────────────────────────────────────────────────────────────────────────
  {
    key: 'sms_new_message', group: 'both', section: 'Messages between client and talent', channel: 'sms',
    name: 'New portal message text', audience: 'Client and talent', when: 'Whenever the other side writes in the booking chat. Up to 2 texts a day.', type: 'new_portal_message',
    fields: [],
    body: 'Love & Photos LLC: You have a new message in your Love and Photos portal.'
  },
  {
    key: 'sms_unread', group: 'both', section: 'Messages between client and talent', channel: 'sms',
    name: 'Unread message text', audience: 'Client and talent', when: 'A chat or staff message still unread after 1 hour. Once a day while it stays unread.', type: 'unread_message',
    fields: [],
    body: 'Love & Photos LLC: You have an unread message in your Love and Photos portal.'
  },
  // ── Talent ───────────────────────────────────────────────────────────────────────────────────────────────
  {
    key: 'booking_confirmation', group: 'talent', section: 'Getting booked', channel: 'email', sender: 'talent', footer: 'no_reply',
    name: "You've Been Booked!", audience: 'Photographer, videographer', when: 'Within the hour after the booking is marked Confirmed. Once.',
    type: 'booking_confirmation',
    fields: [FIRST, TALENT_PORTAL],
    subject: "You've Been Booked!",
    body: `# 🎉 You've Been Booked!

Hi {{first_name}},

Great news — you've officially been booked for an upcoming Love & Photos event!

Please log in to your Talent Portal as soon as possible to review the preliminary details.

[Navigate To Talent Portal Now]({{portal_link}})

We're excited to see the work you produce — and thank you in advance for representing Love & Photos with excellence and professionalism.

If you need assistance, please contact Love & Photos Talent Support at: studio@team.loveandphotos.com

Happy Capturing!
The Love & Photos Team`
  },
  {
    key: 'chat_open_talent', group: 'talent', section: 'Before the event', channel: 'email', sender: 'talent', footer: 'no_reply',
    name: 'Client chat is open (1 month out)', audience: 'Photographer, videographer, 2nd photographer', when: '9 AM Central, 30 days before the event (assigned later: within the hour). Once.',
    type: 'chat_open_talent',
    fields: [FIRST, TALENT_PORTAL],
    subject: 'Your Upcoming Wedding Assignment – Chat Now Open 🎉',
    body: `Hi {{first_name}},

It's officially one month before your assigned event — and that means your client chat is now open!

You're now required to reach out to your couple directly through the portal to introduce yourself and begin discussing their event details. Please take a moment to review their questionnaire prior to your reach out and then send a warm, professional introduction that includes:
- Your first name only (no last names or personal contact info)
- A short note introducing yourself, including your role for their event

**Important Reminders:**
- 🚫 Do not discuss editing, including timelines
- 🚫 If clients ask about anything outside of the event day — such as editing, payments, or general company questions — kindly direct them to Love & Photos Support for assistance

[Login to Your Talent Portal]({{portal_link}})

Also note that you are required to affirm your reach out to the client in the portal so please do so once you have initiated contact with the client.

Thank you for keeping communication professional and on-brand — your first impression sets the tone for an amazing client experience!

If you need assistance, please contact Love & Photos Talent Support at: studio@team.loveandphotos.com

Warmly,
The Love & Photos Team`
  },
  {
    key: 'sms_chat_open_talent', group: 'talent', section: 'Before the event', channel: 'sms',
    name: 'Chat is open text', audience: 'Photographer, videographer, 2nd photographer', when: 'When the event is within a month. Once.', type: 'chat_open',
    fields: [],
    body: 'Love & Photos LLC: Your client chat is now open in your talent portal. Please introduce yourself to the client.'
  },
  {
    key: 'day_before_reminder', group: 'talent', section: 'Before the event', channel: 'email', sender: 'talent', footer: 'no_reply',
    name: 'Your event tomorrow', audience: 'Photographer, videographer, 2nd photographer', when: '9 AM Central, the day before. Once.',
    type: 'day_before_reminder',
    fields: [FIRST, EVENT_DATE, f('role', 'Their role', 'Photographer'), f('couple', 'Couple', 'Summer Robinson'), f('event_time', 'Start time', '4:00 PM'), f('venue', 'Venue', 'The Barn at Waldo'), f('location', 'City, state', 'Waldo, OH'), TALENT_PORTAL],
    subject: 'Reminder: Your Event Tomorrow - {{event_date}}',
    body: `Hi {{first_name}},

Reminder: you're scheduled as **{{role}}** tomorrow, {{event_date}}

**Event Details:**
- Couple: {{couple}}
- Coverage Start Time: {{event_time}}
- Location: {{venue}}, {{location}}

[Login to Talent Portal]({{portal_link}})

Need help? Support: studio@team.loveandphotos.com`
  },
  {
    key: 'upload_nudge', group: 'talent', section: 'After the event: uploading media', channel: 'email', sender: 'talent', footer: 'reply',
    name: 'Upload reminder 1: nudge', audience: 'Photographer, videographer, 2nd photographer', when: '10 AM Mountain, the day after the event. Stops once media is in.',
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('where', 'Where (" in City, ST", blank when unknown)', ' in Waldo, OH'), f('media', 'photos / video', 'photos'), f('link', 'Booking link', 'https://loveandphotos.com/talent/dashboard/bookings?view=past&booking=…')],
    subject: "Uploading your {{media}} from {{client_name}}'s event",
    body: `Hi {{first_name}},

Thank you for shooting {{client_name}}'s event on {{event_date}}{{where}}!

When you have a moment, please upload your {{media}} to the booking's Box folder and submit your job completion form. The button below opens the booking in your talent dashboard, where the upload link and the form are together.

[Open this booking]({{link}})

Thank you!
The Love & Photos Team

${DISREGARD} Questions? Just reply to this email.`
  },
  {
    key: 'upload_heads_up', group: 'talent', section: 'After the event: uploading media', channel: 'email', sender: 'talent', footer: 'reply',
    name: 'Upload reminder 2: heads-up', audience: 'Photographer, videographer, 2nd photographer', when: '10 AM Mountain, day 3 after the event. Once.',
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('media', 'photos / video', 'photos'), f('are', '"are" (photos) / "is" (video)', 'are'), f('link', 'Booking link', 'https://loveandphotos.com/talent/dashboard/bookings?view=past&booking=…')],
    subject: "Friendly heads-up: your {{media}} from {{client_name}}'s event {{are}} due today",
    body: `Hi {{first_name}},

Just a friendly heads-up that your {{media}} from {{client_name}}'s event on {{event_date}} {{are}} due by the end of today.

If you've already uploaded, all that's left is the job completion form. That's how we know your {{media}} {{are}} in.

[Open this booking]({{link}})

Thank you!
The Love & Photos Team

${DISREGARD} Questions? Just reply to this email.`
  },
  {
    key: 'upload_late', group: 'talent', section: 'After the event: uploading media', channel: 'email', sender: 'talent', footer: 'reply',
    // Traci, 1 Oct 2026: keep an "urgent media overdue" email, without the spam-ish parts (emojis, capitals,
    // deductions), with the Box support address. She will tune the wording here in Business Controls.
    name: 'Upload reminder 3: media overdue', audience: 'Photographer, videographer, 2nd photographer', when: "10 AM Mountain, day 4 (the first day media is late). Once; then the coordinator's OVERDUE list.",
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('link', 'Booking link', 'https://loveandphotos.com/talent/dashboard/bookings?view=past&booking=…')],
    subject: "Urgent: media overdue for {{client_name}}'s event",
    body: `Hi {{first_name}},

Your media for {{client_name}}'s event on {{event_date}} was due 3 days after the event, and we haven't received your job completion form yet. Please upload your media and submit the form today so we can deliver to your client on time.

If you've already uploaded, just submit the form and you're all set.

Having trouble uploading to Box? Email ${BOX_SUPPORT_EMAIL} and we'll help sort it out.

[Open this booking]({{link}})

Thank you!
The Love & Photos Team

${DISREGARD} Questions? Just reply to this email.`
  },
  // ── Step 2 (27 Sep 2026): the one-off emails sent by the Node server ─────────────────────────────────────
  {
    key: 'client_booked', group: 'client', section: 'Booking and payment', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: "\"You're Booked!\" confirmation", audience: 'Client', when: "Right after the client's card payment goes through at checkout. Once per booking.",
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('photographer_name', 'Photographer', 'Your Love & Photos Photographer'), EVENT_DATE, f('package', 'Package', 'Photo + Video'), f('payment_plan', 'Payment plan', 'Pay in Full'), f('total', 'Total', '2,450.00'), f('amount_paid', 'Paid today', '500.00'), f('remaining', 'Remaining balance', '1,950.00', true), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: "You're Booked! ✨ Your Wedding Photo Experience with Love & Photos",
    body: `# ✨ You're Booked!
Your wedding photography experience is confirmed

Hi {{customer_name}},

Congratulations! Your booking with {{photographer_name}} has been confirmed, and your payment has been received.

**📅 Event Details**
Event Date: {{event_date}}
Photographer: {{photographer_name}}
Package: {{package}}
Payment Plan: {{payment_plan}}

**💳 Payment Summary**
Total Package: \${{total}}
Amount Paid Today: \${{amount_paid}}
Remaining Balance: \${{remaining}}

**📋 What's Next?**
- Log into your dashboard to view full booking details and track payments
- Review your service agreement and package details
- Your photographer will contact you 1-2 weeks before your event
- Prepare for your special day!

[View Your Dashboard →]({{dashboard_link}})

Questions or concerns? Contact us anytime at support@loveandphotos.com`
  },
  {
    key: 'client_date_held', group: 'client', section: 'Booking and payment', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Your date is held (payment still due)', audience: 'Client', when: "When Admin starts a booking for a client whose card was declined at checkout (Bookings → Unfinished checkouts). Once per booking.",
    fields: [FIRST, EVENT_DATE, f('package', 'Package', '8 hours · Photography'), f('total', 'Amount due', '950.00'), f('set_password_link', 'Set-password link', 'https://loveandphotos.com/reset-password?setup=...')],
    subject: 'Your date is held: one step left to finish your booking',
    body: `# 📅 Your date is held
One step left to finish your booking

Hi {{first_name}},

We saw your card was declined at checkout, so we've held your date for you while you sort it out.

**📅 Your booking**
Event Date: {{event_date}}
Package: {{package}}
Amount due: \${{total}}

**What's next**
1. Set your password with the button below. The link is good for 30 days.
2. Sign in and pay the \${{total}} from your dashboard. You can use the same card once your bank approves it (a quick call to the number on the back of the card usually does it), or a different card.

[Set your password →]({{set_password_link}})

Questions? Just reply to this email.`
  },
  {
    key: 'chat_open_client_assigned', group: 'client', section: 'Planning the event', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Chat is open (sent when talent is assigned)', audience: 'Client', when: 'When talent is assigned and the event is less than a month away. Every assignment.',
    fields: [FIRST, f('event_date', 'Event date (or "your upcoming event")', 'Saturday, October 4, 2026'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: 'Your chat is now open — connect with your team',
    body: `# 💬 Your Chat is Open!
You can now message your photography team

Hi {{first_name}},

As your {{event_date}} wedding approaches, your chat is now open in your client portal. You can use it to connect directly with your photography team — ask questions, share details, or just say hello.

**Ready to chat?**
Head to your dashboard and look for the chat bubble.

[Open My Dashboard →]({{dashboard_link}})

Questions? Contact us at support@loveandphotos.com`
  },
  {
    key: 'raw_footage_ready', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'RAW footage ready', audience: 'Client', when: 'When staff mark a RAW footage add-on delivered. Once; photo and video RAWs go in the same email.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('box_link', 'Box folder link (one link for everything)', 'https://loveandphotos.box.com/s/…', true), f('photo_box_link', 'Photo RAWs link (photo and video sent together)', 'https://loveandphotos.box.com/s/photos…', true), f('video_box_link', 'Video RAWs link (photo and video sent together)', 'https://loveandphotos.box.com/s/video…', true), f('no_link_note', 'Shown when there is no link yet', 'Your photographer will share access to your files shortly.', true)],
    subject: 'Your RAW Footage is Ready!',
    body: `# 📁 Your RAW Footage is Ready
Your unedited files are now available

Hi {{customer_name}},

Your RAW footage package has been delivered! You can now access all your unedited files.

[Access Your RAW Files]({{box_link}})

[Your Photo RAW Files]({{photo_box_link}})

[Your Video RAW Files]({{video_box_link}})

{{no_link_note}}

RAW files are large unedited originals. A photo viewer or editing software is needed to open them.

Questions? Contact us at support@loveandphotos.com`
  },
  {
    // The gallery email (notify-media-delivered). Its wording lived only in the deployed function until 12 Oct 2026; the
    // default is that wording, except it now says photo or video (it said "Photos" on video deliveries too).
    key: 'media_delivered', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <studio@team.loveandphotos.com>', footer: 'no_reply',
    name: 'Media delivered (full)', audience: 'Client', when: 'When Editing presses Deliver with "Partial delivery" unchecked. Once per photo or video gallery.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('media', 'photo or video', 'photo'), f('media_title', 'Photo or Video', 'Photo'), EVENT_DATE, f('location', 'Event location', 'Denver, CO'), f('talent_role', 'Photographer or Videographer', 'Photographer'), f('talent_name', 'Talent name', 'Tamara Lee', true), f('package', 'Package', '8 Hours - Photography'), f('media_link', 'Gallery link', 'https://loveandphotos.pic-time.com/…')],
    subject: '📸 Your Event {{media_title}} Gallery Is Ready!',
    body: `# ✨ Your {{media_title}} Gallery Is Ready!

Hi **{{customer_name}}**,

Great news! Your {{media}} gallery from **{{event_date}}** has been professionally edited and is now ready for you to view and download!

# 📅 Event Details
**Event Date:** {{event_date}}
**Location:** {{location}}
**{{talent_role}}:** {{talent_name}}
**Package:** {{package}}

[View Your {{media_title}} Gallery]({{media_link}})

# 💡 Download Tips
- Download everything to keep it safe on your device
- Share the link with family and friends
- Consider creating prints or photo books of your favorites
- If you have any questions, don't hesitate to reach out!

We hope you love your gallery as much as we loved capturing it! Thank you for choosing Love & Photos to document your special day.

With love and gratitude,
**The Love & Photos Team**

If you need assistance, contact Love & Photos Client Support at support@loveandphotos.com.`
  },
  {
    // Traci's doc, Oct 2026: a "partial delivery" checkbox on Deliver, with its own email. Draft for Traci to edit.
    key: 'media_delivered_partial', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <studio@team.loveandphotos.com>', footer: 'no_reply',
    name: 'Media delivered (partial)', audience: 'Client', when: 'When Editing presses Deliver with "Partial delivery" checked: part of the gallery now, the rest still to come.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('media', 'photo or video', 'photo'), f('media_title', 'Photo or Video', 'Photo'), EVENT_DATE, f('location', 'Event location', 'Denver, CO'), f('talent_role', 'Photographer or Videographer', 'Photographer'), f('talent_name', 'Talent name', 'Tamara Lee', true), f('package', 'Package', '8 Hours - Photography'), f('media_link', 'Gallery link', 'https://loveandphotos.pic-time.com/…')],
    subject: '📸 The First Part of Your {{media_title}} Gallery Is Ready!',
    body: `# ✨ The First Part of Your {{media_title}} Gallery Is Ready

Hi **{{customer_name}}**,

Good news! The first part of your {{media}} gallery from **{{event_date}}** is ready for you to view and download.

[View Your {{media_title}} Gallery]({{media_link}})

**More is on the way.** The rest of your {{media}} gallery is still being edited, and we'll email you again as soon as it is complete. You don't need to do anything in the meantime.

With love and gratitude,
**The Love & Photos Team**

If you need assistance, contact Love & Photos Client Support at support@loveandphotos.com.`
  },
  {
    // Traci, 21 Oct 2026: one email for each kind of media delivery. Photos delivered on a booking whose video is still
    // being edited (drafts for Traci to edit).
    key: 'media_delivered_photos_first', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <studio@team.loveandphotos.com>', footer: 'no_reply',
    name: 'Media delivered (photos ready, video still coming)', audience: 'Client', when: 'When Editing delivers the photos on a booking with video that is not delivered yet. Once.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('media', 'photo or video', 'photo'), f('media_title', 'Photo or Video', 'Photo'), EVENT_DATE, f('location', 'Event location', 'Denver, CO'), f('talent_role', 'Photographer or Videographer', 'Photographer'), f('talent_name', 'Talent name', 'Tamara Lee', true), f('package', 'Package', '8 Hours - Photography'), f('media_link', 'Gallery link', 'https://loveandphotos.pic-time.com/…')],
    subject: '📸 Your Photo Gallery Is Ready! (Your Video Is Still on the Way)',
    body: `# ✨ Your Photo Gallery Is Ready!

Hi **{{customer_name}}**,

Great news! Your photos from **{{event_date}}** have been professionally edited and are ready for you to view and download.

[View Your Photo Gallery]({{media_link}})

**Your video is still being edited.** We'll email you again as soon as it's ready. You don't need to do anything in the meantime.

With love and gratitude,
**The Love & Photos Team**

If you need assistance, contact Love & Photos Client Support at support@loveandphotos.com.`
  },
  {
    // Traci, 21 Oct 2026: the video's own delivery email (a Photo/Video booking's final delivery, or a video-only one)
    key: 'media_delivered_video', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <studio@team.loveandphotos.com>', footer: 'no_reply',
    name: 'Media delivered (video)', audience: 'Client', when: 'When Editing delivers the video. Once.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('media', 'photo or video', 'photo'), f('media_title', 'Photo or Video', 'Photo'), EVENT_DATE, f('location', 'Event location', 'Denver, CO'), f('talent_role', 'Photographer or Videographer', 'Photographer'), f('talent_name', 'Talent name', 'Tamara Lee', true), f('package', 'Package', '8 Hours - Photography'), f('media_link', 'Gallery link', 'https://loveandphotos.pic-time.com/…')],
    subject: '🎬 Your Event Video Is Ready!',
    body: `# ✨ Your Video Is Ready!

Hi **{{customer_name}}**,

Your video from **{{event_date}}** has been edited and is ready for you to watch and download!

[Watch Your Video]({{media_link}})

# 💡 Tips
- Download your video to keep it safe on your device
- Share the link with family and friends

Thank you for choosing Love & Photos to capture your special day.

With love and gratitude,
**The Love & Photos Team**

If you need assistance, contact Love & Photos Client Support at support@loveandphotos.com.`
  },
  {
    // Traci, 21 Oct 2026: delivering a Sneak Peek sent the client nothing
    key: 'sneak_peek_delivered', group: 'client', section: 'After the event', channel: 'email', from: 'Love & Photos <studio@team.loveandphotos.com>', footer: 'no_reply',
    name: 'Sneak Peek delivered', audience: 'Client', when: 'When Editing delivers a Sneak Peek. Once.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('delivery_link', 'Sneak Peek link', 'https://loveandphotos.pic-time.com/…')],
    subject: '✨ Your Sneak Peek Is Ready!',
    body: `# ✨ Your Sneak Peek Is Ready!

Hi **{{customer_name}}**,

A first look at your day is here! Your Sneak Peek highlights are ready to view and download.

[View Your Sneak Peek]({{delivery_link}})

Your full gallery is still being edited, and we'll email you as soon as it's ready.

With love and gratitude,
**The Love & Photos Team**

If you need assistance, contact Love & Photos Client Support at support@loveandphotos.com.`
  },
  {
    key: 'date_change_client', group: 'client', section: 'Changes and cancellations', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Shoot date updated', audience: 'Client', when: 'When the event date changes (client add-on, or staff Edit Date). Once per change.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), f('photographer_name', 'Photographer', 'Tamara'), f('old_date', 'Previous date', 'Friday, September 18, 2026'), f('new_date', 'New date', 'Saturday, October 3, 2026'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: '📅 Your Shoot Date Has Been Updated - Love & Photos',
    body: `# 📅 Shoot Date Updated

Hi {{customer_name}},

Your shoot date with {{photographer_name}} has been successfully updated!

Previous Date: {{old_date}}
New Date: **{{new_date}}**

**📋 What's Next?**
- Your photographer has been notified of the change
- The new date is now reflected in your dashboard
- All other booking details remain the same
- Your photographer will contact you closer to your new event date

⚠️ **Important:** This was your one-time date change. Additional date changes may incur extra fees.

[View Your Dashboard →]({{dashboard_link}})

Questions? Contact us at support@loveandphotos.com`
  },
  {
    key: 'staff_cancel_client', group: 'client', section: 'Changes and cancellations', channel: 'email', footer: 'reply',
    name: 'Booking cancelled by staff', audience: 'Client', when: 'When staff cancel the booking in the console. Once.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), f('reason', 'Reason staff typed', 'Client requested cancellation')],
    subject: 'Booking Cancellation - {{event_date}}',
    body: `# Booking Cancellation

Dear {{customer_name}},

We regret to inform you that your booking for **{{event_date}}** has been cancelled.

**Cancellation Details**
Event Date: {{event_date}}
Location: {{location}}
Reason: {{reason}}

If you have any questions or would like to rebook, please don't hesitate to contact us.

We apologize for any inconvenience this may cause.

Best regards,
Love & Photos Team`
  },
  {
    key: 'gift_session_client', group: 'client', section: 'Gifts and referrals', channel: 'email', footer: 'reply',
    name: 'Gift session from Love & Photos', audience: 'Client', when: 'When staff gift a session. Once.',
    fields: [f('client_name', 'Client name', 'Summer Robinson')],
    subject: 'You have been gifted a photography session — Love & Photos',
    body: `Hi {{client_name}},

Great news — Love & Photos has gifted you a complimentary photography session!

Log into your client portal at loveandphotos.com/dashboard to choose your preferred date and location.

Your gift session date must be at least 31 days from today. Please choose your date in the portal within 2 months to keep the offer active.

— The Love & Photos Team`
  },
  {
    key: 'gift_redemption', group: 'client', section: 'Gifts and referrals', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Gift session redemption', audience: 'The person a client bought a gift for', when: 'When a purchased gift session is paid. Once.',
    fields: [f('recipient_name', 'Recipient name', 'Alex'), f('code', 'Redemption code', 'GIFT-A1B2C3D4'), f('redeem_link', 'Claim link', 'https://loveandphotos.com/gift/redeem/GIFT-A1B2C3D4'), f('expiry_date', 'Expires', 'March 1, 2027')],
    subject: '🎁 Someone gifted you a photography session!',
    body: `# 🎁 You've Got a Gift!
A photography session is waiting for you

Hi {{recipient_name}},

Someone special has gifted you a photography session through Love & Photos. You can redeem it anytime to book a shoot with one of our photographers.

**Your Redemption Code**
{{code}}

[Claim Your Gift]({{redeem_link}})

This gift expires on {{expiry_date}}.

Questions? Contact us at support@loveandphotos.com`
  },
  {
    key: 'new_portal_message_email', group: 'both', section: 'Messages between client and talent', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'no_reply',
    name: 'New portal message', audience: 'Client and talent', when: 'Whenever the other side writes in the booking chat. Every message.',
    fields: [FIRST, f('sender_name', 'Who wrote', 'Tamara Ranslem'), f('message', 'The message (first 500 characters)', 'Hi! I just wanted to introduce myself…'), f('portal_link', 'Portal link', 'https://loveandphotos.com/dashboard')],
    subject: 'New message in your Love & Photos portal',
    body: `# New Portal Message

Hi {{first_name}},

You have a new message from {{sender_name}} in your Love & Photos portal.

{{message}}

[Open Portal]({{portal_link}})

Please log in to reply.`
  },
  {
    key: 'application_approved_new', group: 'talent', section: 'Joining the team', channel: 'email', footer: 'reply',
    name: 'Application approved (new login)', audience: 'Talent applicant without a login yet', when: 'When staff approve an application in the Waiting Room. Once.',
    fields: [FIRST, f('role', 'photographer / videographer', 'photographer'), f('link', 'Set-password link', 'https://loveandphotos.com/reset-password?…')],
    subject: 'Welcome to Love & Photos: your application is approved',
    body: `Hi {{first_name}},

Great news: your application to join Love & Photos as a {{role}} has been approved.

[Set your password here]({{link}})

If the link has expired, go to the login page, choose "Forgot password" and use this email address.

Once you're in, complete the required training to open the Talent Portal and start receiving assignments.

Welcome to the team,
Love & Photos`
  },
  {
    key: 'application_approved_existing', group: 'talent', section: 'Joining the team', channel: 'email', footer: 'reply',
    name: 'Application approved (already has a login)', audience: 'Talent applicant who already has a login', when: 'When staff approve an application in the Waiting Room. Once.',
    fields: [FIRST, f('role', 'photographer / videographer', 'photographer'), f('link', 'Sign-in link', 'https://loveandphotos.com/login')],
    subject: 'Welcome to Love & Photos: your application is approved',
    body: `Hi {{first_name}},

Great news: your application to join Love & Photos as a {{role}} has been approved.

[Sign in here]({{link}})

Once you're in, complete the required training to open the Talent Portal and start receiving assignments.

Welcome to the team,
Love & Photos`
  },
  {
    key: 'application_denied', group: 'talent', section: 'Joining the team', channel: 'email', footer: 'reply',
    name: 'Application denied', audience: 'Talent applicant', when: 'When staff deny an application. Once.',
    fields: [FIRST],
    subject: 'Your Love & Photos application',
    body: `Hi {{first_name}},

Thank you for applying to join Love & Photos. After reviewing your application and portfolio, we're not able to add you to our talent team.

Best,
Love & Photos`
  },
  {
    key: 'application_denied_encourage', group: 'talent', section: 'Joining the team', channel: 'email', footer: 'reply',
    name: 'Application denied (encourage to reapply)', audience: 'Talent applicant', when: 'When staff deny an application with "encourage to reapply". Once.',
    fields: [FIRST],
    subject: 'Your Love & Photos application',
    body: `Hi {{first_name}},

Thank you for applying to join Love & Photos. After reviewing your application and portfolio, we're not able to add you to our talent team right now. We'd encourage you to apply again after gaining additional relevant experience.

Best,
Love & Photos`
  },
  {
    key: 'training_purchase', group: 'talent', section: 'Joining the team', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Training course purchase', audience: 'Course buyer', when: 'When the Photography Fundamentals course is bought. Once.',
    fields: [f('customer_name', 'Buyer name', 'Jordan'), f('course_name', 'Course', 'Photography Fundamentals'), f('amount', 'Amount', '$199.00'), f('order_id', 'Order ID', 'pur_123'), f('course_link', 'Course link', 'https://loveandphotos.com/training/photography-fundamentals')],
    subject: 'Welcome to {{course_name}}! 🎓',
    body: `# 🎓 Welcome to the Course!

Hi {{customer_name}},

Thank you for purchasing {{course_name}}! You now have lifetime access to all 36 modules across Technical, Creative, and Logistic skills.

**Order Details**
Course: {{course_name}}
Amount: {{amount}}
Order ID: {{order_id}}

**What's included:**
- 📸 18 Technical Modules – Master exposure, lighting, and camera settings
- 🎨 12 Creative Modules – Develop your artistic vision and posing skills
- 📦 6 Logistic Modules – Run smooth, professional wedding days
- 🏆 L+P Certification – Pass all assessments to earn verified status

[Start Learning Now]({{course_link}})

If you have any questions, feel free to reach out to us at support@loveandphotos.com.

Happy learning!
– The Love & Photos Team`
  },
  {
    key: 'training_gift', group: 'talent', section: 'Joining the team', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Training course gift', audience: 'Person gifted the course', when: 'When the course is bought as a gift. Once.',
    fields: [f('purchaser', 'Who bought it', 'Jordan Lee'), f('course_name', 'Course', 'Photography Fundamentals'), f('gift_message', 'Their message', 'Happy birthday!', true), f('redeem_link', 'Claim link', 'https://loveandphotos.com/training/redeem?email=…')],
    subject: "🎁 You've received a gift: {{course_name}} Course!",
    body: `# 🎁 You've Received a Gift!

Great news! {{purchaser}} has gifted you access to the {{course_name}} training course!

"{{gift_message}}" – {{purchaser}}

**Your gift includes:**
- ✓ 36 in-depth training modules
- ✓ Technical, Creative & Logistic skills
- ✓ Lifetime access to all content
- ✓ L+P Certification upon completion

[Claim Your Gift]({{redeem_link}})

Click the button above to create your account and start learning. Your gift is waiting for you!

Questions? Contact us at support@loveandphotos.com`
  },
  {
    key: 'talent_booked_assignment', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos <hello@loveandphotos.com>', footer: 'reply',
    name: '"📸 You\'ve been booked!" Photo (at assignment)', audience: 'Photographer, 2nd photographer', when: 'When staff assign the talent, and at checkout to the photographer. Every assignment.',
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('location', 'Location', 'The Barn at Waldo'), f('package', 'Package', 'Photo + Video'), f('dashboard_link', 'Bookings link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: "📸 You've been booked!",
    body: `# 📸 You've been booked!
New photo shoot confirmed

Hi {{first_name}},

You've just been booked for a photo shoot with **{{client_name}}**.

**📅 Shoot Details**
Event Date: {{event_date}}
Event Time: {{event_time}}
Location: {{location}}
Package: {{package}}

**📝 What's Next?**
- Check your Talent Dashboard to view the full booking details and client preferences
- Review the client's style preferences and must-have shots
- Mark your calendar for the event date
- You'll be contacted 1-2 weeks before the event with final details

[View Booking Details →]({{dashboard_link}})

Questions or concerns? Contact support at support@loveandphotos.com`
  },
  {
    // Its own wording for video (Traci, 19 Oct 2026: "create a duplicate of this one so we can put a different message
    // for videographers vs photographers")
    key: 'talent_booked_assignment_video', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos <hello@loveandphotos.com>', footer: 'reply',
    name: '"🎥 You\'ve been booked!" Video (at assignment)', audience: 'Videographer, 2nd videographer', when: 'When staff assign the talent. Every assignment.',
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('location', 'Location', 'The Barn at Waldo'), f('package', 'Package', 'Photo + Video'), f('dashboard_link', 'Bookings link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: "🎥 You've been booked!",
    body: `# 🎥 You've been booked!
New video shoot confirmed

Hi {{first_name}},

You've just been booked for a video shoot with **{{client_name}}**.

**📅 Shoot Details**
Event Date: {{event_date}}
Event Time: {{event_time}}
Location: {{location}}
Package: {{package}}

**📝 What's Next?**
- Check your Talent Dashboard to view the full booking details and client preferences
- Review the client's style preferences and must-have shots
- Mark your calendar for the event date
- You'll be contacted 1-2 weeks before the event with final details

[View Booking Details →]({{dashboard_link}})

Questions or concerns? Contact support at support@loveandphotos.com`
  },
  {
    key: 'date_change_talent', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Booking date changed', audience: 'Primary photographer', when: 'When the event date changes. Once per change.',
    fields: [FIRST, f('client_name', 'Client', 'Summer Robinson'), f('old_date', 'Previous date', 'Friday, September 18, 2026'), f('new_date', 'New date', 'Saturday, October 3, 2026'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: '📅 Booking Date Changed - {{client_name}}',
    body: `# 📅 Booking Date Changed

Hi {{first_name}},

**{{client_name}}** has changed their shoot date using their date change flexibility add-on.

Previous Date: {{old_date}}
New Date: **{{new_date}}**

**📋 Action Required:**
- Update your calendar to reflect the new shoot date
- Review your availability and confirm this date works for you
- Contact the customer if there are any concerns
- All other booking details remain unchanged

[View Booking Details →]({{dashboard_link}})

Need Help? Contact support at support@loveandphotos.com`
  },
  {
    key: 'staff_cancel_talent', group: 'talent', section: 'Changes and cancellations', channel: 'email', footer: 'reply',
    name: 'Booking cancelled by staff', audience: 'Primary photographer', when: 'When staff cancel in the console. Once.',
    fields: [f('talent_name', 'Talent name', 'Tamara Ranslem'), f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), f('reason', 'Reason staff typed', 'Client requested cancellation')],
    subject: 'Booking Cancelled - {{event_date}}',
    body: `# Booking Cancellation Notice

Dear {{talent_name}},

We're writing to inform you that a booking you were assigned to has been cancelled.

**Booking Details**
Client: {{client_name}}
Event Date: {{event_date}}
Location: {{location}}
Reason: {{reason}}

This date is now available in your calendar for other bookings.

If you have any questions, please contact the Love & Photos team.

Best regards,
Love & Photos Team`
  },
  // ── Step 2b (27 Sep 2026): emails sent by the other edge functions ────────────────────────────────────────
  {
    key: 'payment_thank_you', group: 'client', section: 'Booking and payment', channel: 'email', sender: 'client', footer: 'reply',
    name: '"Thank You for Booking"', audience: 'Client', when: 'After a successful payment on the booking.',
    fields: [FIRST, EVENT_DATE, f('location', 'City, state or venue', 'Waldo, OH'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: 'Thank You for Booking with Love & Photos',
    body: `# Thank You for Booking!

Hi {{first_name}},

Thank you for choosing Love & Photos! We've received your payment and are thrilled to be part of your special day.

**Event Details:**
Date: {{event_date}}
Location: {{location}}

**What happens next?**
We will be in touch once your photographer confirms their availability for your date. You can manage everything through your Client Portal.

[Open Your Client Portal]({{dashboard_link}})

If you have any questions, feel free to reach out to us at any time.`
  },
  {
    key: 'client_confirmed', group: 'client', section: 'Booking and payment', channel: 'email', from: 'Love & Photos <no-reply@loveandphotos.com>', footer: 'no_reply',
    name: '"You\'re Confirmed!"', audience: 'Client', when: 'The moment the booking is marked Confirmed. Once.',
    fields: [FIRST, EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), CLIENT_PORTAL],
    subject: "You're Confirmed! Love & Photos — {{event_date}}",
    body: `# Your Booking Is Confirmed 🎉

Hi {{first_name}},

Amazing news — your event is officially confirmed! We're thrilled to be part of your day and will take great care of you from here.

Event Date: {{event_date}}
Location: {{location}}

You can view details, chat when available, and manage everything in your Client Portal.

[Open Your Client Portal]({{portal_link}})`
  },
  {
    // Traci's Loom (28 Sep 2026): "receipt after each withdrawal, stating date of next payment, and remaining balance due"
    key: 'payment_receipt_plan', group: 'client', section: 'Booking and payment', channel: 'email', sender: 'client', footer: 'reply',
    name: 'Payment plan receipt', audience: 'Client', when: 'After each payment-plan charge after the first (the first sends "Thank You for Booking").',
    type: 'payment_receipt_plan',
    fields: [FIRST, f('amount', 'Amount paid', '$99.00'), f('paid_date', 'Date paid', 'November 29, 2026'), EVENT_DATE, f('remaining', 'Remaining balance', '$881.00'), f('next_payment', 'Next payment (amount and date)', '$99.00 on December 29, 2026', true), f('paid_off_note', 'Shown when the booking is paid in full', 'Your booking is now paid in full. Thank you!', true), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: 'Payment received: {{amount}}',
    body: `# Payment Received

Hi {{first_name}},

We received your payment of **{{amount}}** on {{paid_date}} toward your {{event_date}} booking. Thank you!

**Remaining balance:** {{remaining}}
**Next payment:** {{next_payment}}

{{paid_off_note}}

[View Your Payments]({{dashboard_link}})`
  },
  {
    // Traci's Loom (28 Sep 2026): "Shows when they have an upcoming amount due"
    key: 'payment_upcoming', group: 'client', section: 'Booking and payment', channel: 'email', sender: 'client', footer: 'reply',
    name: 'Upcoming payment reminder', audience: 'Client', when: '3 days before each payment-plan charge. Once per charge.',
    type: 'payment_upcoming',
    fields: [FIRST, f('amount', 'Amount due', '$99.00'), f('due_date', 'Charge date', 'December 29, 2026'), EVENT_DATE, f('remaining', 'Remaining balance before this payment', '$881.00'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: 'Upcoming payment: {{amount}} on {{due_date}}',
    body: `# Your Next Payment

Hi {{first_name}},

Just a heads-up: your next payment of **{{amount}}** for your {{event_date}} booking will be charged on **{{due_date}}** to the card on file.

**Remaining balance before this payment:** {{remaining}}

Need to update your card or pay ahead? You can do both from your portal.

[Open Your Portal]({{dashboard_link}})`
  },
  {
    key: 'payment_failed_email', group: 'client', section: 'Booking and payment', channel: 'email', sender: 'client', footer: 'reply',
    name: 'Payment failed', audience: 'Client on a payment plan', when: 'When a scheduled plan payment fails. At most once per calendar month.',
    fields: [FIRST, f('amount_due', 'Amount due', '199.00'), EVENT_DATE, f('grace_end', 'Grace period ends', 'Tuesday, October 7, 2026'), f('update_link', 'Update-payment link', 'https://loveandphotos.com/dashboard')],
    subject: 'Action Required: Payment Failed for Your {{event_date}} Booking',
    body: `# Payment Failed

Hi {{first_name}},

We were unable to process your scheduled payment of \${{amount_due}} for your {{event_date}} event.

**A $15.00 failed payment fee has been applied to your account.**

You have until **{{grace_end}}** to update your payment method.
If payment is not resolved within this 10-day grace period, your booking will be cancelled and all prior payments will be forfeited.

**To resolve this issue:**
Please update your payment method or ensure sufficient funds are available, then we'll automatically retry the payment.

[Update Payment Method Now]({{update_link}})

If you have any questions or need assistance, please contact us immediately at support@loveandphotos.com`
  },
  {
    key: 'mileage_fee_charged', group: 'client', section: 'Planning the event', channel: 'email', from: 'Love & Photos <no-reply@loveandphotos.com>', footer: 'reply',
    name: 'Mileage fee charged', audience: 'Client (staff get a copy)', when: 'When the client submits the questionnaire with venues more than 20 miles apart and the card on file is charged. Once.',
    fields: [f('client_name', 'Client name', 'Summer Robinson'), EVENT_DATE, f('distance', 'Total miles', '34.2'), f('excess', 'Miles over 20', '14.2'), f('amount', 'Fee', '9.94')],
    subject: 'Mileage Fee Charged: ${{amount}}',
    body: `# Mileage Fee Applied

Hi {{client_name}},

Based on the venue addresses you provided in your logistics questionnaire, a mileage fee has been applied and charged to your booking.

Event Date: {{event_date}}
Total Distance: {{distance}} miles
Miles Over Threshold: {{excess}} miles
Mileage Fee: **\${{amount}}**

**Why was this fee applied?**
The distance between your venues exceeds our standard 20-mile coverage area. We charge $0.70 per mile for distances over 20 miles to cover additional travel costs.

Questions about this charge? Contact us at support@loveandphotos.com`
  },
  {
    key: 'mileage_fee_required', group: 'client', section: 'Planning the event', channel: 'email', from: 'Love & Photos <no-reply@loveandphotos.com>', footer: 'reply',
    name: 'Mileage fee due', audience: 'Client (staff get a copy)', when: 'When the client submits the questionnaire with venues more than 20 miles apart and there is no card to charge. Once.',
    fields: [f('client_name', 'Client name', 'Summer Robinson'), EVENT_DATE, f('distance', 'Total miles', '34.2'), f('excess', 'Miles over 20', '14.2'), f('amount', 'Fee', '9.94')],
    subject: 'Additional Mileage Fee Required: ${{amount}}',
    body: `# Mileage Fee Required

Hi {{client_name}},

Based on the venue addresses you provided in your logistics questionnaire, a mileage fee has been added to your booking.

Event Date: {{event_date}}
Total Distance: {{distance}} miles
Miles Over Threshold: {{excess}} miles
Mileage Fee: **\${{amount}}**

**Why was this fee applied?**
The distance between your venues exceeds our standard 20-mile coverage area. We charge $0.70 per mile for distances over 20 miles to cover additional travel costs.

Questions about this charge? Contact us at support@loveandphotos.com`
  },
  {
    key: 'talent_declined_client', group: 'client', section: 'Changes and cancellations', channel: 'email', sender: 'client', footer: 'reply',
    name: 'Photographer no longer available', audience: 'Client', when: 'When the talent declines the job in their portal. Once per decline.',
    fields: [f('customer_name', 'Client name', 'Summer Robinson'), EVENT_DATE, f('days_until_event', 'Days until the event', '12'), f('dashboard_link', 'Dashboard link', 'https://loveandphotos.com/dashboard')],
    subject: 'Important: Your Photographer is No Longer Available',
    body: `# Update to Your Booking
Action needed for your {{event_date}} wedding

Hi {{customer_name}},

Your assigned photographer is no longer available for your {{event_date}} wedding.

**Don't worry - we've got you covered! ✨**
We have {{days_until_event}} days to find you the perfect replacement photographer, and our team is already on it.

**What happens next:**
- **We Find Your New Photographer.** Our team hand-picks a replacement who fits your date, location and style, and confirms with you.
- **We'll Handle Everything.** Your questionnaires and booking details will automatically transfer to your new photographer.
- **Connect & Prepare.** Once assigned, you'll be able to chat with your new photographer directly through your dashboard.

**✓ Travel Fee Waived (bonus):** if your replacement photographer typically charges a travel fee, we'll waive it for you.

[View Your Dashboard →]({{dashboard_link}})

**Need Help?** Our team is here to assist you. Reply to this email or contact us at support@loveandphotos.com`
  },
  {
    key: 'referral_invite', group: 'client', section: 'Gifts and referrals', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply', offer: true,
    name: 'Referral invite', audience: 'A friend the client invites', when: 'When the client sends an invite from their Referrals page. Once per friend; up to 10 a day.',
    fields: [f('referrer_name', 'Who invited them', 'Jane D.'), f('code', 'Invite code', 'JANE200', true), f('track_link', 'View-packages link', 'https://loveandphotos.com/…'), f('opt_out_link', 'Unsubscribe link', 'https://…/opt-out')],
    subject: '{{referrer_name}} invited you to Love & Photos ✨',
    body: `# {{referrer_name}} invited you to Love & Photos ✨

Whether you're planning a wedding, engagement or a special event and want quality photographed memories without the high price tag; Love & Photos makes it simple!

- ✔ Professional photographers
- ✔ Transparent, affordable pricing
- ✔ Flexible payment options
- ✔ Easy online booking and we handle the rest!

**YOUR INVITE CODE:** {{code}} (enter it at checkout when you book)

Explore packages, check availability, and start planning your day.

[View Packages →]({{track_link}})

If you'd prefer not to receive emails like this, you can [unsubscribe here]({{opt_out_link}}).`
  },
  {
    key: 'sms_referral_invite', group: 'client', section: 'Gifts and referrals', channel: 'sms',
    name: 'Referral invite text', audience: 'A friend the client invites', when: 'When the client texts an invite. Each click.',
    fields: [f('referrer_name', 'Who invited them', 'Jane D.'), f('book_link', 'Invite link', 'https://loveandphotos.com/?code=JANE200')],
    body: "{{referrer_name}} invited you to Love & Photos ✨ where it's easy to plan beautiful, stress-free wedding photography & video. View your invite + get started here: {{book_link}} Reply STOP to unsubscribe."
  },
  {
    key: 'referral_payout', group: 'client', section: 'Gifts and referrals', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Referral payout ready', audience: 'Client who referred someone', when: '31 days after the referred booking was confirmed. Once.',
    fields: [f('referrer_name', 'Referrer name', 'Jane Doe'), f('client_name', 'Who they referred', 'your referred friend'), f('dollars', 'Payout', '200'), f('referrals_link', 'Referrals page link', 'https://loveandphotos.com/dashboard/referrals')],
    subject: 'Your ${{dollars}} referral payout is ready!',
    body: `# Good news, {{referrer_name}}! 🎉

Your referral for **{{client_name}}** has passed the 31-day window and your **\${{dollars}}** payout is now ready.

Our team will process your payment via QuickBooks. Make sure your W-9 and banking info are on file — log in to check your onboarding status.

[View My Referrals]({{referrals_link}})`
  },
  {
    key: 'referral_void', group: 'client', section: 'Gifts and referrals', channel: 'email', from: 'Love & Photos <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Referral voided', audience: 'Client who referred someone', when: 'When the referred booking is refunded. Once.',
    fields: [f('referrer_name', 'Referrer name', 'Jane Doe'), f('client_name', 'Who they referred', 'Summer Robinson'), f('reason', 'Why ("a chargeback" / "a cancellation")', 'a cancellation')],
    subject: 'Update on your referral for {{client_name}}',
    body: `# Update on your referral

Hi {{referrer_name}}, unfortunately the booking for **{{client_name}}** was voided due to {{reason}}.

As a result, the referral payout for this booking has been voided. If you have questions, please reach out to our team.`
  },
  {
    key: 'unread_messages_digest', group: 'both', section: 'Messages between client and talent', channel: 'email', from: 'Love & Photos <notifications@loveandphotos.com>', footer: 'no_reply',
    name: 'Unread messages from Love & Photos', audience: 'Client and talent (mostly talent)', when: 'A staff or system message still unread after 1 hour. Checked every hour; once per message, one email per person.',
    fields: [f('full_name', 'Name', 'Tamara Ranslem'), f('count', 'How many', '2'), f('s', '"s" when more than one', 's'), f('messages', 'The messages (who and a preview)', '**From: Nautica**\nHi Tamara! Quick question about Saturday…', false), f('portal_link', 'Portal link', 'https://loveandphotos.com/talent/dashboard')].map(x => (x.name === 'messages' ? { ...x, markup: true } : x)),
    subject: 'You have {{count}} unread message{{s}} from Love & Photos',
    body: `# 💌 New Message{{s}} from Love & Photos

Hi {{full_name}},

You have {{count}} unread message{{s}} waiting for you in your Love & Photos portal.

{{messages}}

[📬 View Messages]({{portal_link}})

Please don't reply to this email. Log in to your portal to respond to messages.`
  },
  {
    key: 'qbo_reminder', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos <billing@lp.loveandphotos.com>', footer: 'reply',
    name: 'Review your QBO payment details', audience: 'Returning talent not yet set up in QuickBooks', when: 'When they are assigned. Every assignment until they are set up.',
    fields: [FIRST, f('booking_date', 'Event date', 'Saturday, October 4, 2026'), f('with_client', '" with {client}" (blank when unknown)', ' with Summer Robinson'), f('qbo_link', 'QuickBooks link', 'https://qbo.intuit.com')],
    subject: 'Review Your QBO Payment Details - New Booking',
    body: `# New Booking - Review Payment Details

Hi {{first_name}},

Congratulations on your upcoming booking on {{booking_date}}{{with_client}}!

**Action Required**
Before this job, please log in to QuickBooks Online to review and confirm your payment details are up to date. This ensures you'll be paid promptly after completing the event.

[Review QBO Details]({{qbo_link}})

If you have any questions about your payment setup, feel free to reach out to our billing team.

Love & Photos Billing Team`
  },
  {
    key: 'style_quiz_done', group: 'talent', section: 'Getting booked', channel: 'email', sender: 'client', footer: 'reply',
    name: 'Client finished the style quiz', audience: 'Photographer', when: 'When the client submits the style quiz. Every submit.',
    fields: [f('photographer_name', 'Photographer name', 'Tamara Ranslem'), f('client_name', 'Client', 'Summer Robinson'), f('style', 'Photography style', 'Light & Airy'), f('mood', 'Color mood', 'Warm'), f('music', 'Music preference', 'Acoustic'), f('shots', 'Must-have shots', '12'), f('notes', 'Special notes', 'Grandma is flying in!', true), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('venue', 'Venue', 'The Barn at Waldo'), f('package', 'Package', 'Photo + Video'), f('job_link', 'Job link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: 'Style Quiz Completed - {{client_name}}',
    body: `# 📋 Client Style Quiz Completed

Hi {{photographer_name}},

Good news! Your client **{{client_name}}** has completed their style quiz for their upcoming session.

**Client Preferences:**
- Photography Style: {{style}}
- Color Mood: {{mood}}
- Music Preference: {{music}}
- Must-Have Shots: {{shots}} selected

**Special Notes:** {{notes}}

**Event Details:**
- Date: {{event_date}}
- Time: {{event_time}}
- Venue: {{venue}}
- Package: {{package}}

[View the Booking]({{job_link}})

Please review these preferences before the event to ensure you can deliver exactly what the client is looking for.`
  },
  {
    key: 'logistics_submitted_talent', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos Notifications <studio@team.loveandphotos.com>', footer: 'reply',
    name: 'Client submitted logistics', audience: 'Photographer, videographer', when: 'When the client submits the logistics questionnaire.',
    fields: [FIRST, f('client_name', 'Couple', 'Summer Robinson'), EVENT_DATE, f('location', 'Location', 'The Barn at Waldo, Waldo, OH'), f('status', 'Questionnaire status', 'submitted'), f('bookings_link', 'Bookings link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: 'Logistics Submitted — {{client_name}} • {{event_date}}',
    body: `# Logistics Submitted

Hi {{first_name}},
Your couple has submitted their logistics questionnaire. We truly appreciate your care and professionalism. These details help you arrive confident and prepared, and help us deliver an exceptional, on-brand experience together.

Couple: {{client_name}}
Wedding Date: {{event_date}}
Location: {{location}}
Questionnaire Status: {{status}}

When you have a moment, please review the latest details in your portal. If anything stands out or you need clarification, feel free to leave a note in the portal chat — our team is here to support you.

**Friendly checklist:**
- Glance through updated timeline, addresses, and key contacts
- Confirm any timing nuances (first look, travel, lighting windows)
- Note any client preferences or special requests

[Review Booking Details]({{bookings_link}})`
  },
  {
    key: 'logistics_updated_talent', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Love & Photos Notifications <studio@team.loveandphotos.com>', footer: 'reply',
    name: 'Client updated logistics', audience: 'Photographer, videographer', when: 'When the client edits a submitted logistics questionnaire.',
    fields: [FIRST, f('client_name', 'Couple', 'Summer Robinson'), EVENT_DATE, f('location', 'Location', 'The Barn at Waldo, Waldo, OH'), f('status', 'Questionnaire status', 'submitted'), f('bookings_link', 'Bookings link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: 'Logistics Updated — {{client_name}} • {{event_date}}',
    body: `# Logistics Updated

Hi {{first_name}},
Great news — your couple just updated their logistics questionnaire. We truly appreciate your care and professionalism. These details help you arrive confident and prepared, and help us deliver an exceptional, on-brand experience together.

Couple: {{client_name}}
Wedding Date: {{event_date}}
Location: {{location}}
Questionnaire Status: {{status}}

When you have a moment, please review the latest details in your portal. If anything stands out or you need clarification, feel free to leave a note in the portal chat — our team is here to support you.

**Friendly checklist:**
- Glance through updated timeline, addresses, and key contacts
- Confirm any timing nuances (first look, travel, lighting windows)
- Note any client preferences or special requests

[Review Booking Details]({{bookings_link}})`
  },
  {
    key: 'booking_change_talent', group: 'talent', section: 'Getting booked', channel: 'email', from: 'Booking Changes <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Booking change (date, hours, add-ons)', audience: 'Photographer, videographer (staff get a copy)', when: 'When the date changes, or a paid change (hours, add-ons) goes through. Once per change.',
    fields: [f('change_label', 'What changed', '⏰ Hours Added'), f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), f('changed_at', 'When', 'Sep 27, 2026, 3:15 PM'), f('summary', 'Change summary', 'Added 2 hours of coverage'), f('link', 'Booking link', 'https://loveandphotos.com/talent/dashboard')],
    subject: '{{change_label}}: {{client_name}} - {{event_date}}',
    body: `# {{change_label}}

**{{client_name}}** has made a change to their booking.

**Booking Details**
Wedding Date: {{event_date}}
Location: {{location}}
Changed At: {{changed_at}}

**Change Summary**
{{summary}}

[View Booking Details]({{link}})`
  },
  {
    key: 'pre_event_quiz', group: 'talent', section: 'Before the event', channel: 'email', sender: 'talent', footer: 'reply',
    name: 'Pre-event quiz', audience: 'Photographer, videographer', when: '2 AM Mountain, 5 days before the event. Once.',
    fields: [f('talent_name', 'Talent name', 'Tamara Ranslem'), f('due_date', 'Due by', 'Mon, Oct 6, 3:00 PM'), f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('venue', 'Venue', 'The Barn at Waldo'), f('location', 'City, state', 'Waldo, OH'), f('role', 'Their role', 'Photographer'), TALENT_PORTAL],
    subject: '📋 Required: Pre-Event Quiz for Upcoming Wedding',
    body: `# 📋 Pre-Event Quiz Required

Hi {{talent_name}},

You have an upcoming wedding in 5 days! Before the event, we need you to complete a quick quiz to confirm your readiness and knowledge of our policies and standards.

**⚠️ Action Required**
Please complete this quiz within 24 hours (by {{due_date}})

**📅 Event Details**
Client: {{client_name}}
Event Date: {{event_date}}
Event Time: {{event_time}}
Venue: {{venue}}
Location: {{location}}
Your Role: {{role}}

**📝 About the Quiz:**
- Takes approximately 5 minutes to complete
- Covers deliverables, policies, equipment, and professional conduct
- Must pass to confirm your readiness for the event
- If not completed within 24 hours, admin team will be notified

[📋 Take Quiz Now]({{portal_link}})

**⏰ Important Deadline**
Failure to complete this quiz within 24 hours will result in notification to the admin team for manual follow-up. This is a required step to confirm your event readiness.

Thank you for your professionalism and commitment to excellence. We're excited for you to capture this beautiful day!

Best regards,
The Love & Photos Talent Team`
  },
  {
    key: 'event_tomorrow_photographer', group: 'talent', section: 'Before the event', channel: 'email', from: 'Love & Photos Talent <support@loveandphotos.com>', footer: 'reply',
    name: 'Event tomorrow: final checklist (photographer)', audience: 'Photographer', when: 'Midnight Mountain, the night before the day before. Once.',
    fields: [f('talent_name', 'Talent name', 'Tamara Ranslem'), f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('venue', 'Venue', 'The Barn at Waldo'), f('location', 'City, state', 'Waldo, OH'), f('hours', 'Hours', '8'), f('overtime_link', 'Overtime form link', 'https://loveandphotos.com/overtime-request'), f('booking_link', 'Booking link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: "🎯 Event Tomorrow: Final Checklist for {{client_name}}'s Wedding",
    body: `# 🎯 Event Tomorrow!
Final Preparation Checklist

Hi {{talent_name}},

Your wedding event is tomorrow! This is your final reminder with important details and requirements to ensure everything goes smoothly.

**📅 Event Details**
Client: {{client_name}}
Date: {{event_date}}
Time: {{event_time}}
Venue: {{venue}}
Location: {{location}}
Coverage: {{hours}} hour(s)
Your Role: Photographer

**✅ Photographer Checklist**
- RAW + JPEG capture - Both formats required for all images
- Shutter speed 1/250 minimum - Prevent motion blur
- 100+ photos per hour minimum - Comprehensive coverage
- Backup equipment ready - Second camera body and extra batteries
- Memory cards formatted - Start fresh with adequate storage
- Professional attire - Dress code appropriate for wedding
- Arrive 15-30 minutes early - Scout location and test equipment

**📋 Professional Conduct Reminders**
- Extremely friendly, confident, and professional at all times
- No guests or assistants - Only assigned talent on site
- Communicate proactively - Keep client and coordinator informed
- Stay hydrated and energized - Take care of yourself
- Capture all key moments - Balance of candids, posed, and details

**⏱️ Overtime Requests**
If the couple requests additional time beyond what's booked: confirm your availability, have them fill out the Overtime Request Form, do NOT accept cash payments on-site, and continue coverage once the form is submitted.

[📋 Overtime Request Form]({{overtime_link}})

**⚠️ Post-Event Requirements**
Within 3 days after the event:
- Upload all media (RAW, JPEG)
- Complete the Job Completion Form
- Failure to meet deadline = breach of contract and potential payment reduction/blacklisting

[📱 View Booking Details]({{booking_link}})

You've got this! We know you're going to create beautiful memories for {{client_name}}. Thank you for representing Love & Photos with excellence and professionalism.

Best of luck tomorrow! 🎉
The Love & Photos Talent Team`
  },
  {
    key: 'event_tomorrow_videographer', group: 'talent', section: 'Before the event', channel: 'email', from: 'Love & Photos Talent <support@loveandphotos.com>', footer: 'reply',
    name: 'Event tomorrow: final checklist (videographer)', audience: 'Videographer', when: 'Midnight Mountain, the night before the day before. Once.',
    fields: [f('talent_name', 'Talent name', 'Brian Hilton'), f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('event_time', 'Start time', '4:00 PM'), f('venue', 'Venue', 'The Barn at Waldo'), f('location', 'City, state', 'Waldo, OH'), f('hours', 'Hours', '8'), f('overtime_link', 'Overtime form link', 'https://loveandphotos.com/overtime-request'), f('booking_link', 'Booking link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: "🎯 Event Tomorrow: Final Checklist for {{client_name}}'s Wedding",
    body: `# 🎯 Event Tomorrow!
Final Preparation Checklist

Hi {{talent_name}},

Your wedding event is tomorrow! This is your final reminder with important details and requirements to ensure everything goes smoothly.

**📅 Event Details**
Client: {{client_name}}
Date: {{event_date}}
Time: {{event_time}}
Venue: {{venue}}
Location: {{location}}
Coverage: {{hours}} hour(s)
Your Role: Videographer

**✅ Videographer Checklist**
- 1080p minimum quality - Ensure all cameras set to correct format
- Time/date accuracy verified - Check all recording equipment settings
- Audio check before key moments - Test before ceremony, speeches, vows
- Full ceremony recording - No cuts, tripod-mounted, clean audio
- Key moment coverage - First look, ceremony, first dance, speeches, etc.
- Backup equipment ready - Extra batteries, memory cards, audio equipment
- Professional attire - Dress code appropriate for wedding
- Arrive 15-30 minutes early - Scout location and test equipment

**📋 Professional Conduct Reminders**
- Extremely friendly, confident, and professional at all times
- No guests or assistants - Only assigned talent on site
- Communicate proactively - Keep client and coordinator informed
- Stay hydrated and energized - Take care of yourself
- Capture all key moments - Balance of candids, posed, and details

**⏱️ Overtime Requests**
If the couple requests additional time beyond what's booked: confirm your availability, have them fill out the Overtime Request Form, do NOT accept cash payments on-site, and continue coverage once the form is submitted.

[📋 Overtime Request Form]({{overtime_link}})

**⚠️ Post-Event Requirements**
Within 3 days after the event:
- Upload all media (RAW, JPEG, video files)
- Complete the Job Completion Form
- Failure to meet deadline = breach of contract and potential payment reduction/blacklisting

[📱 View Booking Details]({{booking_link}})

You've got this! We know you're going to create beautiful memories for {{client_name}}. Thank you for representing Love & Photos with excellence and professionalism.

Best of luck tomorrow! 🎉
The Love & Photos Talent Team`
  },
  {
    key: 'client_cancelled_talent', group: 'talent', section: 'Changes and cancellations', channel: 'email', from: 'Booking Notifications <noreply@loveandphotos.com>', footer: 'reply',
    name: 'Client cancelled', audience: 'Photographer, videographer (staff get their own version)', when: 'When the client cancels. Once.',
    fields: [f('client_name', 'Client', 'Summer Robinson'), EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), f('cancelled_at', 'When', 'Sep 27, 2026, 3:15 PM'), f('reason', 'Reason given', 'Change of plans', true), f('link', 'Bookings link', 'https://loveandphotos.com/talent/dashboard/bookings')],
    subject: '🚫 Booking Cancelled: {{client_name}} - {{event_date}}',
    body: `# 🚫 Booking Cancellation

**{{client_name}}** has cancelled their booking.

**⚠️ Important Information**
- All future billing has been stopped
- No further payments will be collected

**Booking Details**
Client: {{client_name}}
Event Date: {{event_date}}
Location: {{location}}
Cancelled At: {{cancelled_at}}
Reason: {{reason}}

**Next Steps**
- This date is now available for rebooking
- All scheduled payment reminders have been cancelled
- No further action is required from talent
- Client will not be charged any additional fees

[View Your Bookings]({{link}})

Deposits are non-refundable as per your service agreement.`
  },
  {
    key: 'emergency_alert_email', group: 'talent', section: 'Changes and cancellations', channel: 'email', from: 'Emergency Alerts <emergency@loveandphotos.com>', footer: 'reply',
    name: 'Emergency job alert', audience: 'Available talent within 100 miles', when: 'When a talent declines 7 days or less before the event. Once per decline, to everyone who matches.',
    fields: [f('urgency_emoji', '🚨 (3 days or less) / ⚠️', '🚨'), f('urgency_level', 'CRITICAL / HIGH', 'CRITICAL'), f('talent_name', 'Talent name', 'Tamara Ranslem'), f('days', 'Days until the event', '3'), EVENT_DATE, f('location', 'City, state', 'Waldo, OH'), f('distance', 'Miles away', '24'), f('service', 'Service', 'Photography'), f('hours', 'Hours', '8'), f('job_link', 'Job link', 'https://loveandphotos.com/talent/dashboard?emergency_job=…')],
    subject: '{{urgency_emoji}} EMERGENCY JOB ALERT - {{event_date}} ({{days}} days away)',
    body: `# {{urgency_emoji}} EMERGENCY JOB ALERT
{{urgency_level}} PRIORITY - {{days}} days until event

Hi {{talent_name}},

We need your help! A photographer has cancelled on a booking with only {{days}} days until the event. You are within range and available on this date.

**Event Details**
Date: {{event_date}}
Location: {{location}}
Distance from you: {{distance}} miles
Service Type: {{service}}
Hours: {{hours}} hours

**⏰ Time-Sensitive:** This is an emergency replacement request. Please respond as soon as possible if you're interested in taking this job.

[VIEW JOB DETAILS & ACCEPT]({{job_link}})

If you're not interested or unavailable, no action is needed. This alert will be sent to other available photographers in the area.

Thank you for being a reliable part of our team!

Best regards,
Love & Photos Team`
  },
  {
    key: 'sms_emergency_alert', group: 'talent', section: 'Changes and cancellations', channel: 'sms',
    name: 'Emergency job alert text', audience: 'Available talent within 100 miles', when: 'Same as the email above.',
    fields: [f('urgency_emoji', '🚨 / ⚠️', '🚨'), EVENT_DATE, f('days', 'Days until the event', '3'), f('location', 'City, state', 'Waldo, OH'), f('distance', 'Miles away', '24'), f('service', 'Service', 'Photography'), f('job_link', 'Job link', 'https://loveandphotos.com/talent/dashboard?emergency_job=…')],
    body: "{{urgency_emoji}} EMERGENCY JOB ALERT from Love & Photos! Event: {{event_date}} ({{days}} days away). Location: {{location}} ({{distance}} mi from you). Service: {{service}}. A photographer cancelled - you're available & nearby! View details: {{job_link}}"
  },
  // ── Portal chat (Traci, 2 Nov 2026: "add the site chat messages to the business tools > controls > notifications"):
  // the messages the system itself writes into a client's or talent's portal chat. The defaults are the wording that
  // was hard-coded in each sender until then. Plain text: line breaks stay, nothing else is formatted.
  {
    key: 'chat_talent_assigned', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Assigned to a booking', audience: 'Talent', when: 'When the team assigns talent to a booking (first assignment).',
    fields: [f('role', 'Role', 'photographer'), f('client', "Client's (possessive)", "Summer Robinson's"), f('event_type', 'Event type', 'wedding'), f('event_date', 'Event date', '2026-10-04')],
    body: 'You\'ve been assigned as the {{role}} for {{client}} {{event_type}} on {{event_date}}. Please review the booking details in your portal and click the green "Confirm Seen" button on the bookings tab of your portal.'
  },
  {
    key: 'chat_talent_assigned_replacement', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Assigned as a replacement', audience: 'Talent', when: 'When the team assigns talent in place of someone who dropped or declined.',
    fields: [f('role', 'Role', 'photographer'), f('client', "Client's (possessive)", "Summer Robinson's"), f('event_type', 'Event type', 'wedding'), f('event_date', 'Event date', '2026-10-04')],
    body: 'You\'ve been assigned as the replacement {{role}} for {{client}} {{event_type}} on {{event_date}}. Please review the booking details in your portal and click the green "Confirm Seen" button on the bookings tab of your portal.'
  },
  {
    key: 'chat_new_booking_talent', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'New booking confirmed', audience: 'Talent', when: 'When a client pays for a booking with talent already on it.',
    fields: [f('client_name', 'Client name', 'Summer Robinson'), f('event_date', 'Event date', '2026-10-04')],
    body: 'New booking confirmed! {{client_name}} has booked you for their wedding on {{event_date}}.'
  },
  {
    key: 'chat_reminder_client_contact', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Remind: contact the client', audience: 'Talent', when: 'Talent Coordinator clicks Remind on Client Contact Needed. Also sent as a text.',
    fields: [FIRST, f('client_name', 'Client name', 'Summer Robinson'), f('event_date', 'Event date', 'Saturday, October 4')],
    body: 'Hi {{first_name}}, this is Love & Photos. Friendly reminder to reach out to {{client_name}} through your portal chat about their {{event_date}} event. Client contact is due 3 weeks before the event.'
  },
  {
    key: 'chat_reminder_five_day_checkin', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Remind: 5-day check-in', audience: 'Talent', when: 'Talent Coordinator clicks Remind on 5-Day Check-In Needed. Also sent as a text.',
    fields: [FIRST, f('client_name', 'Client name', 'Summer Robinson'), f('days_away', 'When (tomorrow / in 5 days)', 'in 5 days'), f('event_date', 'Event date', 'Saturday, October 4')],
    body: "Hi {{first_name}}, this is Love & Photos. {{client_name}}'s event is {{days_away}} ({{event_date}}). Please reply in your portal to confirm you're all set."
  },
  {
    key: 'chat_reminder_day_of_checkin', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Remind: day-of check-in', audience: 'Talent', when: 'Talent Coordinator clicks Remind on Day-Of Check-In Due. Also sent as a text.',
    fields: [FIRST, f('client_name', 'Client name', 'Summer Robinson')],
    body: "Hi {{first_name}}, this is Love & Photos. Today is {{client_name}}'s event. Please reply in your portal to confirm you're on your way."
  },
  {
    key: 'chat_reminder_insurance_form', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Remind: insurance form', audience: 'Talent', when: 'Talent Coordinator clicks Remind on Insurance Form Needed. Also sent as a text.',
    fields: [FIRST, f('client_name', 'Client name', 'Summer Robinson'), f('event_date', 'Event date', 'Saturday, October 4')],
    body: "Hi {{first_name}}, this is Love & Photos. Please complete your insurance form for {{client_name}}'s {{event_date}} event in your portal. It's needed before the event."
  },
  {
    key: 'chat_reminder_media_overdue', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Remind: media and form overdue', audience: 'Talent', when: 'Talent Coordinator clicks Remind on OVERDUE Media/Form. Also sent as a text.',
    fields: [FIRST, f('client_name', 'Client name', 'Summer Robinson'), f('event_date', 'Event date', 'Saturday, October 4'), f('due_days', 'Days after the event media is due', '14')],
    body: "Hi {{first_name}}, this is Love & Photos. Your media and job completion form for {{client_name}}'s {{event_date}} event were due {{due_days}} days after the event. Please upload and submit them today."
  },
  {
    key: 'chat_talent_removed_location', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Removed after a location change', audience: 'Talent', when: "When a client's new event location changes the travel fee and the talent is taken off the booking.",
    fields: [f('booking_id', 'Booking id', '21616052-9042-474c-b4e2-f85723617648'), f('location', 'New city, state', 'Fontana, CA')],
    body: 'Hi, we wanted to let you know that your assignment to booking {{booking_id}} has been removed because the client changed their event location to {{location}}. This booking will be reassigned to another team member. Thank you for your understanding.'
  },
  {
    key: 'chat_forfeit_talent', group: 'talent', section: 'Portal chat', channel: 'chat',
    name: 'Booking forfeited (questionnaire)', audience: 'Talent', when: "When a client's booking is forfeited for an incomplete logistics questionnaire.",
    fields: [f('client_name', 'Client name', 'Summer Robinson'), EVENT_DATE],
    body: '📅 Booking cancelled: Client {{client_name}} forfeited services. Event was {{event_date}}. This date is now available.'
  },
  {
    key: 'chat_questionnaire_reminder', group: 'client', section: 'Portal chat', channel: 'chat',
    name: 'Logistics questionnaire reminder', audience: 'Client', when: 'With each questionnaire deadline reminder email (14, 12, 10 and 8 days before the event).',
    fields: [f('days_left', 'Days until it locks', '5')],
    body: '⏰ Complete your logistics questionnaire! Only {{days_left}} days left before it locks.'
  },
  {
    key: 'chat_forfeit_client', group: 'client', section: 'Portal chat', channel: 'chat',
    name: 'Booking forfeited (questionnaire)', audience: 'Client', when: 'When the booking is forfeited for an incomplete logistics questionnaire.',
    fields: [EVENT_DATE],
    body: '❌ Your wedding services for {{event_date}} have been forfeited due to incomplete questionnaire. Please contact support if you have questions.'
  },
  // ── Vendors (Traci's Loom, 28 Sep 2026): follow-ups to the vendors a couple listed on the logistics questionnaire,
  // 3 days, 30 days and 6 months after the wedding, by email and text. Replies go to the business email and number
  // set under Vendor follow-ups on this tab, which GoHighLevel reads; nothing is sent until both are set.
  {
    key: 'vendor_followup_3d_email', group: 'vendor', section: 'After the wedding', channel: 'email', sender: 'client', footer: 'reply',
    name: '3 days after: email', audience: 'Wedding vendors', when: '3 days after the wedding, to each vendor the couple listed.',
    type: 'vendor_followup_3d',
    fields: [VENDOR_NAME, COUPLE, EVENT_DATE],
    subject: "Loved working with you at {{couple_name}}'s wedding",
    body: `Hey {{vendor_name}},

Hope you've been well since {{couple_name}}'s wedding on {{event_date}}. We loved working alongside you!

We get asked for vendor recommendations all of the time and would love to include you on our list. We'd appreciate if you would also keep us in mind for couples looking for affordable photo and video options, too!

Thanks again!
The Love and Photos Team`
  },
  {
    key: 'vendor_followup_3d_sms', group: 'vendor', section: 'After the wedding', channel: 'sms',
    name: '3 days after: text', audience: 'Wedding vendors', when: 'Same as the email above.',
    type: 'vendor_followup_3d',
    fields: [VENDOR_NAME, COUPLE],
    body: "Hi {{vendor_name}}! This is Love and Photos. We loved working with you at {{couple_name}}'s wedding! Please keep us in mind when you get asked for photographer and videographer recommendations so we can work together again soon. And we'll do the same! :)"
  },
  {
    key: 'vendor_followup_30d_email', group: 'vendor', section: 'After the wedding', channel: 'email', sender: 'client', footer: 'reply',
    name: '30 days after: email', audience: 'Wedding vendors', when: '30 days after the wedding, to each vendor the couple listed.',
    type: 'vendor_followup_30d',
    fields: [VENDOR_NAME, COUPLE, EVENT_DATE],
    subject: "Loved working with you at {{couple_name}}'s wedding",
    body: `Hey {{vendor_name}},

Hope you've been well since {{couple_name}}'s wedding on {{event_date}}.

Do you know of any couples looking for affordable wedding photo and video right now? We'd love to be on your list of folks you recommend.`
  },
  {
    key: 'vendor_followup_30d_sms', group: 'vendor', section: 'After the wedding', channel: 'sms',
    name: '30 days after: text', audience: 'Wedding vendors', when: 'Same as the email above.',
    type: 'vendor_followup_30d',
    fields: [VENDOR_NAME],
    body: "Hey {{vendor_name}}. Do you know of any couples looking for affordable wedding photo and video right now? We'd love to be on your list of folks you recommend."
  },
  {
    key: 'vendor_followup_6m_email', group: 'vendor', section: 'After the wedding', channel: 'email', sender: 'client', footer: 'reply',
    name: '6 months after: email', audience: 'Wedding vendors', when: '6 months after the wedding, to each vendor the couple listed.',
    type: 'vendor_followup_6m',
    fields: [VENDOR_NAME, COUPLE],
    subject: 'Saying hello from Love & Photos',
    body: `Hi {{vendor_name}},

It's been a little while since we worked together at {{couple_name}}'s wedding, and we wanted to check in and say hello!

We really enjoyed working with you and would love to cross paths again. If you have any couples looking for affordable wedding photography or videography, we'd always be happy to help.

Hope wedding season is treating you well, and we look forward to working together again!

Best,
The Love and Photos Team`
  },
  {
    key: 'vendor_followup_6m_sms', group: 'vendor', section: 'After the wedding', channel: 'sms',
    name: '6 months after: text', audience: 'Wedding vendors', when: 'Same as the email above.',
    type: 'vendor_followup_6m',
    fields: [VENDOR_NAME, COUPLE],
    body: "Hi {{vendor_name}}! Love and Photos here. It's been a little while since {{couple_name}}'s wedding, and we wanted to say hello! We loved working with you and hope we get to work together again soon! I've got you on my list of recommendations. Hope we're on yours!"
  }
]

const BY_KEY = new Map(CATALOG.map(t => [t.key, t]))
export const catalogEntry = key => BY_KEY.get(key) || null

/** The template a sender uses: the catalog default with an Admin edit (a notification_templates row) laid over it. */
export function resolveTemplate(key, rows = []) {
  const base = BY_KEY.get(key)
  if (!base) return null
  const row = (Array.isArray(rows) ? rows : [...(rows?.values?.() || [])]).find(r => r && r.key === key)
  return {
    ...base,
    enabled: row ? row.enabled !== false : true,
    subject: row?.subject ?? base.subject ?? null,
    body: row?.body ?? base.body,
    customized: Boolean(row && (row.subject != null || row.body != null)),
    updated_at: row?.updated_at || null,
    updated_by_name: row?.updated_by_name || null
  }
}

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const FIELD_RE = /\{\{\s*([a-z_]+)\s*\}\}/g

/** Field names used in a piece of wording. */
export function fieldsUsed(text) {
  return [...new Set([...String(text || '').matchAll(FIELD_RE)].map(m => m[1]))]
}

/**
 * Problems that must stop a save: fields the template does not have, empty wording, a text that is far too long.
 * Warnings (a long text that will split into several messages) are returned separately.
 */
export function checkTemplate(key, { subject, body }) {
  const t = BY_KEY.get(key)
  const errors = []
  const warnings = []
  if (!t) return { errors: ['Unknown notification'], warnings }
  const known = new Set(t.fields.map(x => x.name))
  const unknown = fieldsUsed(`${subject || ''}\n${body || ''}`).filter(n => !known.has(n))
  if (unknown.length) errors.push(`Not a field for this message: ${unknown.map(n => `{{${n}}}`).join(', ')}`)
  if (!String(body || '').trim()) errors.push('The wording cannot be empty')
  if (t.channel === 'email' && !String(subject || '').trim()) errors.push('The subject cannot be empty')
  if (t.channel === 'sms') {
    const len = renderSms({ ...t, body }, sampleValues(t)).length
    if (len > 480) errors.push(`Too long for a text (${len} characters; keep it under 480)`)
    else if (len > 160) warnings.push(`${len} characters: this goes out as ${Math.ceil(len / 153)} texts`)
  }
  if (t.channel === 'chat' && renderChat({ ...t, body }, sampleValues(t)).length > 2000) errors.push('Too long for a chat message (keep it under 2,000 characters)')
  return { errors, warnings }
}

export function sampleValues(t) {
  return Object.fromEntries((t?.fields || []).map(x => [x.name, x.sample]))
}

/**
 * Fill the fields. In the HTML version a value is plain text: it cannot open a button, a link, bold or a bullet, and a
 * line break inside it stays inside its paragraph (a chat message quoted in an email, 27 Sep 2026). A field marked
 * `markup` (a list the sender builds) keeps its formatting.
 */
function fill(text, values, { html, markup = new Set() }) {
  return String(text || '').replace(FIELD_RE, (_, name) => {
    const v = values[name]
    const s = v === null || v === undefined ? '' : String(v)
    if (!html) return s
    if (markup.has(name)) return esc(s)
    return esc(s).replace(/[[\]*#]/g, c => `&#${c.charCodeAt(0)};`).replace(/\n+/g, ' ').replace(/^-\s/, '&#45; ')
  })
}

/** Lines that use an optional field with no value are left out (e.g. "Total: {{total}}" when there is no total). */
function dropEmptyOptionalLines(text, t, values) {
  const optional = new Set((t.fields || []).filter(x => x.optional).map(x => x.name))
  return String(text || '').split('\n').filter(line => {
    const used = fieldsUsed(line).filter(n => optional.has(n))
    return !used.some(n => values[n] === null || values[n] === undefined || String(values[n]).trim() === '')
  }).join('\n')
}

function inline(s) {
  // s is already HTML-escaped (fields included)
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" style="color:#5b2a86;">$1</a>')
}

const P = 'margin:0 0 16px 0;font-size:16px;line-height:1.6;color:#333;'

/** The wording as HTML blocks (paragraphs, headings, bullets, buttons). */
function bodyHtml(text) {
  const blocks = String(text || '').replace(/\r\n/g, '\n').split(/\n{2,}/)
  const out = []
  for (const block of blocks) {
    const lines = block.split('\n').filter(l => l.trim() !== '')
    if (!lines.length) continue
    const button = lines.length === 1 && lines[0].trim().match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
    if (button) {
      out.push(`<p style="margin:28px 0;text-align:center;"><a href="${button[2]}" style="background:#5b2a86;color:#ffffff;padding:13px 30px;border-radius:6px;text-decoration:none;display:inline-block;font-weight:bold;font-size:16px;">${button[1]}</a></p>`)
      continue
    }
    let para = []
    let list = []
    const flushPara = () => { if (para.length) { out.push(`<p style="${P}">${para.map(inline).join('<br/>')}</p>`); para = [] } }
    const flushList = () => { if (list.length) { out.push(`<ul style="margin:0 0 16px 0;padding-left:22px;font-size:16px;line-height:1.6;color:#333;">${list.map(l => `<li>${inline(l)}</li>`).join('')}</ul>`); list = [] } }
    for (const raw of lines) {
      const line = raw.trimEnd()
      if (/^#\s+/.test(line)) { flushPara(); flushList(); out.push(`<h2 style="margin:0 0 16px 0;font-size:22px;color:#5b2a86;">${inline(line.replace(/^#\s+/, ''))}</h2>`) }
      else if (/^\s*-\s+/.test(line)) { flushPara(); list.push(line.replace(/^\s*-\s+/, '')) }
      else { flushList(); para.push(line) }
    }
    flushPara(); flushList()
  }
  return out.join('\n')
}

/** Plain-text version of the wording, for the email's text part. */
function bodyText(text) {
  return String(text || '')
    .replace(/^#\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^\[([^\]]+)\]\(([^)\s]+)\)$/gm, '$1: $2')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1 ($2)')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * An email ready to send: { from, subject, html, text }. The frame (header, footer, unsubscribe line on offers) is
 * fixed; only the wording and subject come from the template.
 */
export function renderEmail(t, values = {}) {
  const htmlValues = values
  const kept = dropEmptyOptionalLines(t.body, t, values)
  const subject = fill(t.subject, values, { html: false }).replace(/\s+/g, ' ').trim()
  const markup = new Set((t.fields || []).filter(x => x.markup).map(x => x.name))
  const escapedBody = fill(kept.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])), htmlValues, { html: true, markup })
  const footerLines = []
  if (t.footer === 'no_reply') footerLines.push("This is an automated message. Replies to this email aren't monitored or received.")
  if (t.offer) footerLines.push(OFFER_FOOTER)
  footerLines.push(BUSINESS_ADDRESS)
  footerLines.push('www.loveandphotos.com')
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
<div style="padding:22px 30px;border-bottom:3px solid #5b2a86;text-align:center;font-size:13px;letter-spacing:3px;font-weight:bold;color:#5b2a86;">LOVE &amp; PHOTOS</div>
<div style="padding:30px;">
${bodyHtml(escapedBody)}
</div>
<div style="background:#f9fafb;padding:18px 30px;text-align:center;">
${footerLines.map(l => `<p style="margin:4px 0;font-size:12px;line-height:1.5;color:#6b7280;">${l.startsWith('www.') ? `<a href="https://www.loveandphotos.com" style="color:#9ca3af;text-decoration:none;">${l}</a>` : esc(l)}</p>`).join('\n')}
</div>
</div>`
  const text = [fill(bodyText(kept), values, { html: false }), '', ...footerLines].join('\n')
  return { from: t.from || SENDERS[t.sender] || SENDERS.client, subject, html, text }
}

/** A text ready to send. Always ends with the STOP line (added when the wording does not already say STOP). */
/** A portal chat message: plain text, line breaks kept, no STOP line (it is not a text message). */
export function renderChat(t, values = {}) {
  return fill(dropEmptyOptionalLines(t.body, t, values), values, { html: false }).replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim()
}

export function renderSms(t, values = {}) {
  const body = fill(dropEmptyOptionalLines(t.body, t, values), values, { html: false }).replace(/\s+/g, ' ').trim()
  return /\bSTOP\b/.test(body) ? body : `${body} ${STOP_LINE}`
}

/**
 * Load every Admin edit once. Never throws: no table yet, or a database error, means "use the defaults".
 * @returns rows [{ key, enabled, subject, body, updated_at, updated_by_name }]
 */
export async function loadTemplateRows(supabase) {
  try {
    const { data, error } = await supabase.from('notification_templates').select('key, enabled, subject, body, updated_at, updated_by_name')
    if (error) return []
    return data || []
  } catch {
    return []
  }
}
